/*
 * <BeDataTable> -- React binding for .be-data-table, the plain-<table> treatment,
 * as opposed to <BeTable>'s R5 column-header look.
 *
 * It exists to own the scroll wrapper: a table's columns will not break, so one
 * without a wrapper widens the whole document (#412).
 */

import type { CSSProperties, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { type BeGrowable, type BeSpace, space } from "./Stack.tsx";

export type BeDataTableProps = BeCommon &
	BeGrowable & {
		children?: ReactNode;
		/** The table's own label. Rendered as a real <caption>. */
		caption?: ReactNode;
		/** Every column but the last is a quiet annotation of the last. */
		tags?: boolean;
		/** Inset between the table and the edge of a wrapper the caller tints. */
		pad?: BeSpace;
		style?: CSSProperties;
	};

/* className/style/ref land on the WRAPPER -- it is the box a caller places, tints
 * or grows, and the element that scrolls. */
export const BeDataTable = beComponent<BeDataTableProps, HTMLDivElement>("BeDataTable", (props, be) => {
	const { children, caption, tags, pad, grow, scale, className, style, ...rest } = props;
	const root = be("be-data-table-scroll", grow && "be-grow");
	return (
		<div {...rest} {...root} style={pad ? ({ ...root.style, "--be-pad": space(pad) } as CSSProperties) : root.style}>
			<table className={tags ? "be-data-table be-data-table--tags" : "be-data-table"}>
				{caption ? <caption>{caption}</caption> : null}
				{children}
			</table>
		</div>
	);
});
